import type { ExportDocumentInfo } from '@ctw/shared/content/fact-extraction/compute-export-summary';
import { strToU8, type Zippable, zip } from 'fflate';

export interface ExportDocumentResult {
  document: ExportDocumentInfo;
  outcome: 'included' | 'failed' | 'unsupported';
  // Why the document isn't in the zip; set for failed/unsupported outcomes.
  reason?: string;
  // Zip entry name (under documents/); set for included outcomes by assignFileNames.
  fileName?: string;
  bytes?: Uint8Array;
}

// Document titles come from source systems and can't be trusted as file
// names. Allowlist approach: fold accented letters to their base form, then
// collapse every run of non-portable characters — whitespace, reserved path
// chars, control chars, and Unicode punctuation (em dashes, curly quotes,
// ellipses) — into a single hyphen, so the result survives shells, scripts,
// and URLs unquoted.
const MAX_FILE_NAME_LENGTH = 80;
const TRAILING_SEPARATORS = /[-.]+$/;

function sanitizeFileNamePart(name: string): string {
  return (
    name
      // é → e, ü → u: decompose, then drop the combining marks so accented
      // letters stay readable instead of turning into hyphens below.
      .normalize('NFKD')
      .replace(/[\u0300-\u036F]/g, '')
      .replace(/[^A-Za-z0-9._]+/g, '-')
      .replace(/^[-.]+|[-.]+$/g, '')
      .slice(0, MAX_FILE_NAME_LENGTH)
      .replace(TRAILING_SEPARATORS, '')
  );
}

export function sanitizeFileName(name: string): string {
  return sanitizeFileNamePart(name) || 'Document';
}

// Joins sanitized parts with hyphens and fits the result under
// MAX_FILE_NAME_LENGTH by shortening the longest part first. Shortening the
// longest rather than the last keeps a report name and date suffix intact when
// a long patient name would otherwise push them off the end.
export function joinFileNameParts(parts: Array<string | undefined>): string {
  // A part that sanitizes to nothing (punctuation, whitespace) is dropped, not
  // replaced by the fallback; only the whole name falls back.
  const sanitized = parts
    .map((part) => sanitizeFileNamePart(part ?? ''))
    .filter((part) => part.length > 0);
  const separators = Math.max(sanitized.length - 1, 0);
  const totalLength = () => sanitized.reduce((sum, part) => sum + part.length, 0) + separators;
  while (totalLength() > MAX_FILE_NAME_LENGTH) {
    const longest = sanitized.reduce(
      (best, part, index) => (part.length > sanitized[best].length ? index : best),
      0,
    );
    const secondLongest = Math.max(
      0,
      ...sanitized.filter((_, index) => index !== longest).map((part) => part.length),
    );
    const excess = totalLength() - MAX_FILE_NAME_LENGTH;
    // Cut only down to the next-longest part, so several long parts share the
    // trimming; always cut at least one character so the loop terminates.
    const shortenTo = Math.min(
      sanitized[longest].length - 1,
      Math.max(secondLongest, sanitized[longest].length - excess),
    );
    sanitized[longest] = sanitized[longest].slice(0, shortenTo).replace(TRAILING_SEPARATORS, '');
  }
  return sanitized.join('-') || 'Document';
}

export function assignFileNames(results: Array<ExportDocumentResult>): void {
  // Collision detection is case-insensitive: zip entries that differ only by
  // case silently overwrite each other when extracted on the case-insensitive
  // filesystems macOS and Windows default to.
  const used = new Set<string>();
  for (const result of results) {
    if (result.outcome !== 'included') {
      continue;
    }
    const base = sanitizeFileName(
      [result.document.title, result.document.date].filter(Boolean).join(' '),
    );
    let candidate = `${base}.pdf`;
    let counter = 2;
    while (used.has(candidate.toLowerCase())) {
      candidate = `${base}-${counter}.pdf`;
      counter += 1;
    }
    used.add(candidate.toLowerCase());
    result.fileName = candidate;
  }
}

// Human-oriented record of what the export contains and why anything is missing.
export function buildManifest({
  patientName,
  patientId,
  upid,
  jobId,
  reportDisplayName,
  generatedAt,
  includedFieldsCount,
  excludedFieldsCount,
  documentedIncludedCount,
  results,
}: {
  patientName: string;
  // Builder-owned FHIR Patient resource id — more stable than the UPID,
  // which identity resolution can reassign.
  patientId: string;
  upid: string;
  jobId: string;
  reportDisplayName?: string;
  generatedAt: Date;
  includedFieldsCount: number;
  excludedFieldsCount: number;
  documentedIncludedCount: number;
  results: Array<ExportDocumentResult>;
}): string {
  const lines = [
    'Atlas Export Manifest',
    '=====================',
    `Patient: ${patientName}`,
    `Patient ID: ${patientId}`,
    `UPID: ${upid}`,
    `Generated: ${generatedAt.toISOString()}`,
    `Extraction job: ${jobId}${reportDisplayName ? ` (${reportDisplayName})` : ''}`,
    `Fields: ${includedFieldsCount} included (${documentedIncludedCount} documented), ${excludedFieldsCount} excluded`,
    '',
    `Documents (${results.filter((result) => result.outcome === 'included').length} of ${results.length} included):`,
  ];
  // One block per document: status + headline (file name when included,
  // title otherwise), then indented details, with a blank line between
  // blocks so long titles and reasons stay readable.
  for (const result of results) {
    lines.push('');
    lines.push(`  [${result.outcome}]  ${result.fileName ?? result.document.title}`);
    if (result.fileName) {
      lines.push(`      title:  ${result.document.title}`);
    }
    lines.push(`      binary: ${result.document.binaryId}`);
    if (result.document.documentReferenceId) {
      // Directly fetchable from ODS as DocumentReference/{id}.
      lines.push(`      document: DocumentReference/${result.document.documentReferenceId}`);
    }
    if (result.reason) {
      lines.push(`      reason: ${result.reason}`);
    }
    if (result.document.citationRefs && result.document.citationRefs.length > 0) {
      // The join key back to the CSV's citations column.
      lines.push(`      related resources: ${result.document.citationRefs.join(', ')}`);
    }
  }
  return `${lines.join('\n')}\n`;
}

export async function buildExportZip({
  csv,
  manifest,
  results,
}: {
  csv: string;
  manifest: string;
  results: Array<ExportDocumentResult>;
}): Promise<Blob> {
  const entries: Zippable = {
    // Text entries compress well; PDFs are already compressed, so store them
    // (level 0) instead of burning main-thread time recompressing.
    'atlas-report.csv': [strToU8(csv), { level: 6 }],
    'manifest.txt': [strToU8(manifest), { level: 6 }],
  };
  for (const result of results) {
    if (result.outcome === 'included' && result.fileName && result.bytes) {
      entries[`documents/${result.fileName}`] = [result.bytes, { level: 0 }];
    }
  }
  const zipped = await new Promise<Uint8Array>((resolve, reject) => {
    zip(entries, (error, data) => (error ? reject(error) : resolve(data)));
  });
  return new Blob([zipped as BlobPart], { type: 'application/zip' });
}
